import React, { useState } from "react";
import { TimelineBatch } from "../../../types/mes";
import { t } from "../../../i18n/locale";
import { phaseName } from "./phaseDisplay";
import { applyBatchEdits, getBatchKey, groupBatchesByProduct } from "./planningUtils";

interface Props {
  batches: Partial<TimelineBatch>[];
  onApply: (updated: Partial<TimelineBatch>[]) => void;
  disabled?: boolean;
  formatTime: (dateStr?: string) => string;
}

type Edit = { redoslijed: number; mass_kg: number };

type PhaseCell = { code: string; name: string; start?: string; end?: string };

const phaseLabel = (code: string, fallbackName?: string) => {
  const translated = phaseName(code);
  if (translated !== code) return translated;
  return fallbackName ? t(fallbackName) : code;
};

/**
 * Ordered phases for a batch. Prefers the dynamic phase segments (only the
 * phases the product actually has, incl. packaging/dividing/...); falls back to
 * the legacy 5 fixed fields for saved timelines that don't carry `Phases`,
 * dropping zero-length phases so unused steps aren't shown.
 */
const getBatchPhases = (batch: Partial<TimelineBatch>): PhaseCell[] => {
  if (batch.Phases && batch.Phases.length > 0) {
    return batch.Phases.map((p) => ({
      code: p.Code,
      name: phaseLabel(p.Code, p.Name),
      start: p.Start,
      end: p.End,
    }));
  }
  const legacy: PhaseCell[] = [
    { code: "mixing", name: phaseName("mixing"), start: batch.MixingStart, end: batch.MixingEnd },
    { code: "bulk_fermentation", name: phaseName("bulk_fermentation"), start: batch.FermentationStart, end: batch.FermentationEnd },
    { code: "processing", name: phaseName("processing"), start: batch.ProcessingStart, end: batch.ProcessingEnd },
    { code: "final_proof", name: phaseName("final_proof"), start: batch.FinalProofStart, end: batch.FinalProofEnd },
    { code: "baking", name: phaseName("baking"), start: batch.BakingStart, end: batch.BakingEnd },
  ];
  return legacy.filter((p) => p.start && p.end && p.start !== p.end);
};

/** Column set for a product group: the richest batch's phases, unique by code. */
const phaseColumnsFor = (productBatches: Partial<TimelineBatch>[]): PhaseCell[] => {
  let best: PhaseCell[] = [];
  for (const b of productBatches) {
    const ph = getBatchPhases(b);
    if (ph.length > best.length) best = ph;
  }
  const seen = new Set<string>();
  return best.filter((p) => (seen.has(p.code) ? false : (seen.add(p.code), true)));
};

/**
 * Step 3 of the planning wizard: edit the schedule per product across its
 * phases. Each product is a card whose columns are the product's ACTUAL phases
 * (from the dynamic phase model) — so a semi-finished with only mixing +
 * fermentation shows just those, and a product with packaging shows it too,
 * instead of a fixed 5-phase grid. Order and mass stay editable; applying
 * recomputes phase times via the timeline engine.
 */
const TimelinePhaseEditor: React.FC<Props> = ({ batches, onApply, disabled, formatTime }) => {
  const groups = groupBatchesByProduct(batches);
  const [edits, setEdits] = useState<Record<number, Edit>>({});

  const getEdit = (batch: Partial<TimelineBatch>, index: number): Edit => {
    const key = getBatchKey(batch, index);
    const existing = edits[key];
    return {
      redoslijed: existing?.redoslijed ?? batch.Sequence ?? index + 1,
      mass_kg: existing?.mass_kg ?? batch.FinalMassKg ?? 0,
    };
  };

  const setEdit = (
    batch: Partial<TimelineBatch>,
    index: number,
    field: keyof Edit,
    value: number
  ) => {
    const key = getBatchKey(batch, index);
    setEdits((prev) => ({
      ...prev,
      [key]: { ...getEdit(batch, index), [field]: value },
    }));
  };

  const handleApply = () => {
    const allEdits = batches.map((batch, index) => ({
      batchKey: getBatchKey(batch, index),
      ...getEdit(batch, index),
    }));
    onApply(applyBatchEdits(batches, allEdits));
    setEdits({});
  };

  const phaseCell = (seg?: PhaseCell) => {
    if (!seg) return "-";
    const start = formatTime(seg.start);
    const end = formatTime(seg.end);
    if (start === "-" && end === "-") return "-";
    return `${start}–${end}`;
  };

  if (Object.keys(groups).length === 0) {
    return (
      <div className="mes-section">
        <p style={{ color: "#95a5a6" }}>
          {t("Nema zamjesa za uređivanje. Vrati se na korak „Proizvodi“.")}
        </p>
      </div>
    );
  }

  return (
    <div className="mes-section timeline-phase-editor">
      <div className="phase-editor-head">
        <div>
          <h3 style={{ margin: 0 }}>{t("Uređivanje rasporeda po proizvodu")}</h3>
          <p className="editor-hint" style={{ margin: "4px 0 0" }}>
            {t("Po proizvodu prilagodi redoslijed i masu zamjesa; prikazane su sve procesne faze. Klikni „Primijeni izmjene“ za preračun vremena.")}
          </p>
        </div>
        <button type="button" className="btn-primary" onClick={handleApply} disabled={disabled}>
          {t("Primijeni izmjene")}
        </button>
      </div>

      {Object.entries(groups).map(([product, productBatches]) => {
        const phaseCols = phaseColumnsFor(productBatches);
        return (
        <div key={product} className="product-block">
          <h4>🍞 {product} <span className="count-badge">{t("{count} zamjesa", { count: productBatches.length })}</span></h4>
          <div style={{ overflowX: "auto" }}>
            <table className="mes-table timeline-table phase-table">
              <thead>
                <tr>
                  <th>{t("Red.")}</th>
                  <th>{t("ID zamjesa")}</th>
                  <th>{t("Mikser")}</th>
                  <th>{t("Pećnica")}</th>
                  {phaseCols.map((p) => (
                    <th key={p.code}>{p.name}</th>
                  ))}
                  <th>{t("Masa (kg)")}</th>
                </tr>
              </thead>
              <tbody>
                {productBatches.map((batch) => {
                  const index = batches.indexOf(batch);
                  const edit = getEdit(batch, index);
                  const byCode = new Map(getBatchPhases(batch).map((p) => [p.code, p]));
                  return (
                    <tr key={batch.MixCode || index}>
                      <td>
                        <input
                          type="number"
                          min={1}
                          className="input-classic input-sm"
                          value={edit.redoslijed}
                          onChange={(e) =>
                            setEdit(batch, index, "redoslijed", Number(e.target.value))
                          }
                          disabled={disabled}
                        />
                      </td>
                      <td><code className="batch-id-code">{batch.MixCode}</code></td>
                      <td><span className="mixer-badge">{batch.MixerName || "-"}</span></td>
                      <td>{batch.OvenName || "-"}</td>
                      {phaseCols.map((col) => (
                        <td key={col.code}>{phaseCell(byCode.get(col.code))}</td>
                      ))}
                      <td>
                        <input
                          type="number"
                          min={0.1}
                          step={0.1}
                          className="input-classic input-sm"
                          value={edit.mass_kg}
                          onChange={(e) =>
                            setEdit(batch, index, "mass_kg", Number(e.target.value))
                          }
                          disabled={disabled}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        );
      })}

      <style>{`
        .timeline-phase-editor .phase-editor-head {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 16px;
          margin-bottom: 12px;
        }
        .timeline-phase-editor .product-block { margin-top: 18px; }
        .timeline-phase-editor .product-block h4 {
          display: flex;
          align-items: center;
          gap: 10px;
          margin: 0 0 8px;
          color: #ecf0f1;
        }
        .timeline-phase-editor .count-badge {
          font-size: 12px;
          font-weight: 500;
          color: #95a5a6;
          background: #34495e;
          border-radius: 10px;
          padding: 2px 10px;
        }
        .timeline-phase-editor .phase-table th,
        .timeline-phase-editor .phase-table td { white-space: nowrap; }
        .timeline-phase-editor .phase-table .input-sm { width: 70px; }
      `}</style>
    </div>
  );
};

export default TimelinePhaseEditor;
